import { ArrowLeft, FileText, PackageCheck, PackageX, Printer, Wallet } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, EmptyState } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { appUrl } from '../../lib/basePath.js';
import { formatDate, formatDateTime, formatNumber } from '../../lib/intl.js';
import { formatMoney, trimMoney } from '../../lib/money.js';

import type { ReturnDetail, ReturnSource } from './returnsData.js';

const SOURCE_TONE: Record<ReturnSource, 'info' | 'secondary' | 'warning'> = { till: 'info', register: 'warning', office: 'secondary' };

/**
 * One return, read like the paper it is: who brought what back, against which
 * invoice, where it was rung up, the cash that went back and the shelf the
 * goods went back to. Print opens the same paper as an invoice, titled as a
 * return (DocumentPrintPage).
 */
export function ReturnDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<ReturnDetail | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setDoc(null);
    setMissing(false);
    void api().get<ReturnDetail>(`/returns/${id}`).then((r) => setDoc(r.data)).catch(() => setMissing(true));
  }, [id]);

  const locale = i18n.language;
  const back = (
    <Link to="/sales/returns" className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
      <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
      {t('returns.detail.back', { defaultValue: 'All returns' })}
    </Link>
  );

  if (missing) {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <EmptyState
          icon={<FileText className="h-8 w-8" aria-hidden />}
          title={t('returns.detail.notFound', { defaultValue: "This return doesn't exist, or it isn't in your branch." })}
        />
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <Card className="p-6"><Skeleton className="h-48 w-full" /></Card>
      </div>
    );
  }

  const ccy = doc.currencyCode;
  const money = (v: string | number | null | undefined): string => formatMoney(String(v ?? 0), { locale, currency: ccy });
  const source = doc.source ?? 'office';
  const sourceLabel = source === 'till' ? t('returns.source.till', { defaultValue: 'Till' })
    : source === 'register' ? t('returns.source.register', { defaultValue: 'Register' })
      : t('returns.source.office', { defaultValue: 'Office' });
  const units = (n: string | number): string => t('returns.unitsN', { defaultValue: '{{n}} pcs', n: formatNumber(Number(n), { locale }) });
  const total = Number(doc.total ?? 0);
  // Netted only in the return's own currency; a refund paid in other notes is
  // shown as it was, and the account line is left out rather than guessed.
  const sameCcyRefund = !doc.refund || doc.refund.currencyCode === ccy;
  const onAccount = Math.max(0, total - (doc.refund && sameCcyRefund ? Number(doc.refund.amount) : 0));

  const fact = (label: string, value: ReactNode, sub?: ReactNode): JSX.Element => (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">{label}</span>
      <span className="text-sm font-medium text-fg">{value}</span>
      {sub && <span className="text-xs text-fg-muted">{sub}</span>}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {back}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {t('returns.detail.title', { defaultValue: 'Return' })} <span className="font-mono">{doc.docNumber}</span>
            </h1>
            <Badge variant={SOURCE_TONE[source]}>{sourceLabel}</Badge>
          </div>
          <p className="mt-1 text-sm text-fg-muted">
            {doc.createdAt ? formatDateTime(doc.createdAt, { locale }) : doc.docDate ? formatDate(doc.docDate, { locale }) : ''}
            {doc.createdByName ? ` · ${doc.createdByName}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {doc.creditOfInvoiceId && (
            <Link to={`/sales/invoices/${doc.creditOfInvoiceId}`}>
              <Button variant="secondary"><FileText className="h-4 w-4" aria-hidden />{t('returns.detail.openInvoice', { defaultValue: 'Original invoice' })}</Button>
            </Link>
          )}
          <Button onClick={() => window.open(appUrl(`/sales/invoices/${doc.id}/print`), '_blank')}>
            <Printer className="h-4 w-4" aria-hidden />{t('returns.detail.print', { defaultValue: 'Print' })}
          </Button>
        </div>
      </header>

      <Card className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
        {fact(
          t('returns.col.customer', { defaultValue: 'Customer' }),
          doc.partyId ? (
            <Link to={`/sales/parties/${doc.partyId}`} className="text-brand underline-offset-4 hover:underline">{doc.partyName ?? '—'}</Link>
          ) : (doc.partyName ?? '—'),
          [doc.partyCode, doc.partyPhone].filter(Boolean).join(' · ') || undefined,
        )}
        {fact(
          t('returns.col.invoice', { defaultValue: 'Original invoice' }),
          doc.creditOfInvoiceId ? (
            <Link to={`/sales/invoices/${doc.creditOfInvoiceId}`} className="font-mono text-brand underline-offset-4 hover:underline">{doc.originalNumber ?? '—'}</Link>
          ) : '—',
          doc.originalDate
            ? `${formatDate(doc.originalDate, { locale })}${doc.originalTotal ? ` · ${money(doc.originalTotal)}` : ''}`
            : undefined,
        )}
        {fact(t('returns.col.branch', { defaultValue: 'Branch' }), doc.warehouseName ?? '—')}
        {fact(t('returns.col.reason', { defaultValue: 'Reason' }), doc.notes?.trim() || '—')}
      </Card>

      <Card className="overflow-hidden p-0">
        <div className="border-b border-border px-4 py-3 text-sm font-semibold">{t('returns.detail.lines', { defaultValue: 'Returned items' })}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-subtle text-xs text-fg-muted">
              <tr>
                <th className="px-4 py-2 text-start font-medium">#</th>
                <th className="px-4 py-2 text-start font-medium">{t('returns.detail.code', { defaultValue: 'Code' })}</th>
                <th className="px-4 py-2 text-start font-medium">{t('returns.line.item', { defaultValue: 'Item' })}</th>
                <th className="px-4 py-2 text-start font-medium">{t('returns.detail.unit', { defaultValue: 'Unit' })}</th>
                <th className="px-4 py-2 text-end font-medium">{t('returns.detail.qty', { defaultValue: 'Qty' })}</th>
                <th className="px-4 py-2 text-end font-medium">{t('returns.line.price', { defaultValue: 'Price' })}</th>
                <th className="px-4 py-2 text-end font-medium">{t('returns.detail.lineTotal', { defaultValue: 'Total' })}</th>
              </tr>
            </thead>
            <tbody>
              {doc.lines.map((l, i) => (
                <tr key={l.lineNo} className="border-t border-border">
                  <td className="px-4 py-2 text-fg-subtle">{i + 1}</td>
                  <td className="px-4 py-2 font-mono text-xs text-fg-muted">{l.itemCode ?? '—'}</td>
                  <td className="px-4 py-2 font-medium">{l.itemName ?? l.description ?? '—'}</td>
                  <td className="px-4 py-2 text-fg-muted">{l.unitName ?? '—'}</td>
                  <td className="px-4 py-2 text-end font-mono tabular-nums">{trimMoney(l.quantity)}</td>
                  <td className="px-4 py-2 text-end font-mono tabular-nums">{money(l.unitPrice)}</td>
                  <td className="px-4 py-2 text-end font-mono tabular-nums font-semibold">
                    {money(l.lineTotal ?? Number(l.quantity) * Number(l.unitPrice))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="flex flex-col gap-3 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            {doc.restock ? <PackageCheck className="h-4 w-4 text-success" aria-hidden /> : <PackageX className="h-4 w-4 text-fg-subtle" aria-hidden />}
            {t('returns.detail.stock', { defaultValue: 'Stock' })}
          </div>
          <p className="text-sm text-fg">
            {doc.restock
              ? t('returns.detail.restockedTo', {
                defaultValue: '{{units}} put back in {{warehouse}}',
                units: units(doc.restock.units),
                warehouse: doc.restock.warehouseName ?? '—',
              })
              : t('returns.detail.notRestocked', { defaultValue: 'Not put back in stock' })}
          </p>
        </Card>
        <Card className="flex flex-col gap-2 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Wallet className="h-4 w-4 text-fg-muted" aria-hidden />
            {t('returns.detail.money', { defaultValue: 'Money' })}
          </div>
          <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
            {doc.subtotal && Number(doc.subtotal) !== total && (
              <>
                <dt className="text-fg-muted">{t('returns.detail.subtotal', { defaultValue: 'Subtotal' })}</dt>
                <dd className="text-end font-mono tabular-nums">{money(doc.subtotal)}</dd>
              </>
            )}
            {Number(doc.discountTotal ?? 0) > 0 && (
              <>
                <dt className="text-fg-muted">{t('returns.detail.discount', { defaultValue: 'Discount' })}</dt>
                <dd className="text-end font-mono tabular-nums">−{money(doc.discountTotal)}</dd>
              </>
            )}
            {Number(doc.taxTotal ?? 0) > 0 && (
              <>
                <dt className="text-fg-muted">{t('returns.detail.tax', { defaultValue: 'Tax' })}</dt>
                <dd className="text-end font-mono tabular-nums">{money(doc.taxTotal)}</dd>
              </>
            )}
            <dt className="font-semibold">{t('returns.detail.total', { defaultValue: 'Total credited' })}</dt>
            <dd className="text-end font-mono font-semibold tabular-nums">{money(total)}</dd>
            {doc.refund && (
              <>
                <dt className="text-fg-muted">
                  {t('returns.detail.refunded', { defaultValue: 'Cash refunded' })}
                  <span className="ms-1 font-mono text-xs text-fg-subtle">{doc.refund.number}</span>
                </dt>
                <dd className="text-end font-mono tabular-nums">{formatMoney(doc.refund.amount, { locale, currency: doc.refund.currencyCode })}</dd>
              </>
            )}
            {sameCcyRefund && (
              <>
                <dt className="text-fg-muted">{t('returns.detail.onAccount', { defaultValue: "Left on the customer's account" })}</dt>
                <dd className="text-end font-mono tabular-nums">{money(onAccount)}</dd>
              </>
            )}
          </dl>
        </Card>
      </div>
    </div>
  );
}
